import { useCallback, useEffect, useState } from 'react';
import { Building, Loader2, MapPin, Plus, Trash2 } from 'lucide-react';
import {
  financialInstitutionApiService,
  type InstitutionBranch,
  type InstitutionBranchInput,
} from '../../services/financialInstitutionApi';
import { useToast } from '../../hooks/useToast';

/**
 * Le réseau d'agences d'une institution financière.
 *
 * Pourquoi cette section existe : une institution n'est pas un point unique.
 * Une COOPEC opère dans une zone délimitée, une banque couvre plusieurs
 * provinces, et savoir OÙ une institution est réellement présente décide de ce
 * qu'on peut lui adresser comme dossiers.
 *
 * Ce que cette section remplace : le formulaire demandait auparavant de choisir
 * une « institution mère » dans la liste de TOUTES les institutions de la
 * plateforme. Deux problèmes, pas un : cette liste exposait le portefeuille
 * client à n'importe quel inscrit, et rien n'empêchait un nouveau venu de se
 * déclarer agence d'une banque qui ne le connaît pas. Une institution déclare
 * désormais ses propres agences, depuis son propre dossier.
 */

const BRANCH_TYPES = [
  { value: 'headquarters', label: 'Siège social' },
  { value: 'main_branch', label: 'Agence principale' },
  { value: 'branch', label: 'Agence' },
  { value: 'sub_branch', label: 'Sous-agence' },
  { value: 'agency', label: 'Point de service' },
  { value: 'kiosk', label: 'Kiosque' },
  { value: 'atm_point', label: 'Point ATM' },
  { value: 'mobile_branch', label: 'Agence mobile' },
  { value: 'representative_office', label: 'Bureau de représentation' },
  { value: 'liaison_office', label: 'Bureau de liaison' },
];

const BRANCH_STATUSES = [
  { value: 'active', label: 'En activité' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'temporarily_closed', label: 'Fermée temporairement' },
  { value: 'under_renovation', label: 'En travaux' },
  { value: 'closed', label: 'Fermée' },
];

const EMPTY_BRANCH: InstitutionBranchInput = {
  name: '',
  code: '',
  type: 'branch',
  status: 'active',
  address: { street: '', city: '', province: '', country: 'RDC' },
  contact: { phone: '', email: '' },
  manager: { name: '', phone: '' },
};

interface Props {
  institutionId?: string;
  /** Le dossier est-il en cours d'édition ? Sinon, lecture seule. */
  isEditing: boolean;
}

export function InstitutionBranchesSection({ institutionId, isEditing }: Props) {
  const { showToast } = useToast();
  const [branches, setBranches] = useState<InstitutionBranch[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState<InstitutionBranchInput>(EMPTY_BRANCH);

  const load = useCallback(async () => {
    if (!institutionId) return;
    setLoading(true);
    try {
      setBranches(await financialInstitutionApiService.listBranches(institutionId));
    } catch {
      // Une liste d'agences indisponible ne doit pas bloquer le reste du
      // dossier : on laisse la section vide plutôt que d'alarmer.
      setBranches([]);
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleAdd = async () => {
    if (!institutionId) return;
    if (!draft.name.trim() || !draft.code.trim() || !draft.address.city.trim()) {
      showToast({
        type: 'error',
        title: 'Agence incomplète',
        message: 'Le nom, le code et la ville sont nécessaires.',
      });
      return;
    }

    setSaving(true);
    try {
      await financialInstitutionApiService.addBranch(institutionId, draft);
      showToast({ type: 'success', title: 'Agence ajoutée', message: draft.name });
      setDraft(EMPTY_BRANCH);
      setShowForm(false);
      await load();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "L'agence n'a pas pu être enregistrée.";
      showToast({ type: 'error', title: 'Ajout impossible', message });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (branch: InstitutionBranch) => {
    if (!institutionId) return;
    setSaving(true);
    try {
      await financialInstitutionApiService.removeBranch(institutionId, branch.id);
      showToast({ type: 'success', title: 'Agence fermée', message: branch.name });
      await load();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "L'agence n'a pas pu être fermée.";
      showToast({ type: 'error', title: 'Fermeture impossible', message });
    } finally {
      setSaving(false);
    }
  };

  if (!institutionId) {
    return (
      <section className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-2 flex items-center">
          <Building className="w-5 h-5 mr-2 text-[#197ca8]" />
          Réseau d'agences
        </h2>
        <p className="text-sm text-gray-600">
          Enregistrez d'abord votre institution. Vous pourrez ensuite déclarer
          vos agences et points de service, une par une.
        </p>
      </section>
    );
  }

  return (
    <section className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            <Building className="w-5 h-5 mr-2 text-[#197ca8]" />
            Réseau d'agences
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Les points de service où votre institution est réellement présente.
          </p>
        </div>
        {isEditing && !showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center px-3 py-2 text-sm bg-[#197ca8] text-white rounded-md hover:bg-[#156a92] transition-colors"
          >
            <Plus className="w-4 h-4 mr-1" />
            Ajouter une agence
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Chargement des agences...
        </div>
      ) : branches.length === 0 ? (
        <p className="text-sm text-gray-500">Aucune agence déclarée pour le moment.</p>
      ) : (
        <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md">
          {branches.map((branch) => (
            <li key={branch.id} className="flex items-start justify-between gap-4 p-4">
              <div className="min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {branch.name}
                  <span className="ml-2 text-xs font-normal text-gray-500">{branch.code}</span>
                </p>
                <p className="text-sm text-gray-600 flex items-center mt-1">
                  <MapPin className="w-3.5 h-3.5 mr-1 flex-shrink-0 text-gray-400" />
                  {[branch.address?.street, branch.address?.city, branch.address?.province]
                    .filter(Boolean)
                    .join(', ') || 'Adresse non précisée'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {BRANCH_TYPES.find((t) => t.value === branch.type)?.label || branch.type}
                  {' • '}
                  {BRANCH_STATUSES.find((s) => s.value === branch.status)?.label || branch.status}
                  {branch.manager?.name ? ` • ${branch.manager.name}` : ''}
                </p>
              </div>
              {isEditing && (
                <button
                  type="button"
                  onClick={() => handleRemove(branch)}
                  disabled={saving}
                  className="text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                  title="Fermer cette agence"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {isEditing && showForm && (
        <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label="Nom de l'agence *"
              value={draft.name}
              onChange={(v) => setDraft({ ...draft, name: v })}
              placeholder="Agence de Goma"
            />
            <Field
              label="Code *"
              value={draft.code}
              onChange={(v) => setDraft({ ...draft, code: v })}
              placeholder="AGC-GOM-001"
            />
            <Select
              label="Type"
              value={draft.type}
              options={BRANCH_TYPES}
              onChange={(v) => setDraft({ ...draft, type: v })}
            />
            <Select
              label="Statut"
              value={draft.status}
              options={BRANCH_STATUSES}
              onChange={(v) => setDraft({ ...draft, status: v })}
            />
            <Field
              label="Ville *"
              value={draft.address.city}
              onChange={(v) => setDraft({ ...draft, address: { ...draft.address, city: v } })}
              placeholder="Goma"
            />
            <Field
              label="Province"
              value={draft.address.province}
              onChange={(v) => setDraft({ ...draft, address: { ...draft.address, province: v } })}
              placeholder="Nord-Kivu"
            />
            <Field
              label="Adresse"
              value={draft.address.street || ''}
              onChange={(v) => setDraft({ ...draft, address: { ...draft.address, street: v } })}
              placeholder="Avenue du Lac, n° 12"
            />
            <Field
              label="Téléphone"
              value={draft.contact?.phone || ''}
              onChange={(v) => setDraft({ ...draft, contact: { ...draft.contact, phone: v } })}
              placeholder="+243 ..."
            />
            <Field
              label="Responsable"
              value={draft.manager?.name || ''}
              onChange={(v) => setDraft({ ...draft, manager: { ...draft.manager, name: v } })}
              placeholder="Nom et prénom"
            />
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setDraft(EMPTY_BRANCH);
              }}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleAdd}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 text-sm bg-[#197ca8] text-white rounded-md hover:bg-[#156a92] disabled:opacity-50"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer l'agence
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8]"
      />
    </label>
  );
}

function Select({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8]"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}
